import { SvgIcon } from 'components/helpers/SvgIcon';
import styles from './RegionSelector.module.scss';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Accordion } from 'components/authorable/shared/content/Accordion/Accordion';
import {
  BottomSheetProps,
  Country,
  CountryNameOverRideData,
  Region,
  RegionSelectorProps,
} from '../../types';
import { useRouter } from 'next/router';
import { AccordionItemData } from 'components/helpers/Accordion/AccordionContext';
import { CurrentSelection } from '../CurrentSelection/CurrentSelection';

/**
 * Language codes that should use English display names instead of native names.
 * 'nl' (Dutch): Using English prevents "Nederlands" from being displayed;
 * instead shows "Dutch" for better readability in the region selector UI.
 */
const langCodeOverRide = ['nl'];

const getNativeLanguageName = (
  languageCode?: string,
  countryCode?: string
): { languageName: string; countryName: string } => {
  let languageName = '';
  let countryName = '';

  if (languageCode) {
    try {
      const langCode = languageCode.split('-')[0];
      const displayNames = new Intl.DisplayNames(
        [langCodeOverRide.includes(langCode) ? 'en' : langCode],
        { type: 'language' }
      );
      const native = displayNames.of(langCode) || languageCode;
      languageName = native
        .split(/[-\s]+/)
        .map((part) => (part ? part[0].toUpperCase() + part.slice(1).toLowerCase() : ''))
        .join(' ');
    } catch (error) {
      console.error('Error getting native language name:', error);
      languageName = languageCode;
    }
  }

  if (countryCode) {
    try {
      const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
      countryName = regionNames.of(countryCode.toUpperCase()) || countryCode;
    } catch (error) {
      console.error('Error getting country name:', error);
      countryName = countryCode || '';
    }
  }

  return { languageName, countryName };
};

const hasAlternateName = (
  overrideList?: CountryNameOverRideData[],
  countryName?: string
): string => {
  if (!overrideList || !countryName || overrideList.length === 0) {
    return '';
  }
  const foundItem = overrideList.find(
    (item) => item.displayName?.toLowerCase() === countryName.toLowerCase()
  );

  return foundItem?.fields?.otherCountryName?.value || '';
};

const getLanguagesArray = (country: Country) => {
  const resultArray = [];
  const domainValue =
    country?.fields?.regionSelectorDomain?.value || country?.fields?.domain?.value;
  resultArray.push({
    name: getNativeLanguageName(country?.fields?.defaultLocale?.fields['Regional Iso Code'].value)
      .languageName,
    // Default locale is served at the domain root, so don't append its regional ISO code.
    domain: `${domainValue}`,
    lang: country?.fields?.defaultLocale?.fields['Regional Iso Code'].value,
  });

  country?.fields?.otherLocales?.map((item) => {
    resultArray.push({
      name: getNativeLanguageName(item.fields['Regional Iso Code'].value).languageName,
      domain: `${domainValue}/${item.fields['Regional Iso Code'].value}`,
      lang: item.fields['Regional Iso Code'].value,
    });
  });
  return resultArray;
};

// getCountryName is now handled via getNativeLanguageName's second parameter

// Main Component
export const RegionSelector = ({ regionSelectorData }: RegionSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const [currentLocale, setCurrentLocale] = useState<{
    countryName?: string;
    languageName?: string;
  }>({});

  useEffect(() => {
    // Get locale from Next.js router (same as context.locale)
    const locale = router.locale; // e.g., 'pt-pt', 'en-gb', 'fr-fr'

    if (locale) {
      const [, country] = locale.split('-');

      const { languageName, countryName } = getNativeLanguageName(locale, country?.toUpperCase());
      setCurrentLocale({ countryName, languageName });
    } else {
      setCurrentLocale({
        countryName: 'No Selection',
        languageName: 'No Selection',
      });
    }
  }, [router.locale]); // Re-run when locale changes

  const handleOpenModal = () => setIsOpen(true);
  const handleCloseModal = () => setIsOpen(false);
  return (
    <div className={styles.regionSelectorContainer}>
      <label className={styles.regionSelectorHeadingFooter}>
        {regionSelectorData?.fields.Heading?.value}:
      </label>
      <button
        className={styles.locationSelectorButton}
        onClick={handleOpenModal}
        aria-expanded={isOpen}
        aria-controls="region-accordion"
        aria-label="Open region selector"
      >
        <div>
          <SvgIcon icon="locationSelector" className={styles.locationIcon} />
        </div>
        <div className={styles.countryLanguageContainer}>
          <span className={styles.regionSelectorCountryFooter}>{currentLocale?.countryName}</span>
          <span className={styles.regionSelectorLanguageFooter}>{currentLocale?.languageName}</span>
        </div>
      </button>
      <BottomSheet
        isOpen={isOpen}
        onClose={handleCloseModal}
        regionSelectorData={regionSelectorData}
        currentSelection={currentLocale}
      />
    </div>
  );
};

const BottomSheet = ({
  isOpen,
  onClose,
  regionSelectorData,
  currentSelection,
}: BottomSheetProps) => {
  const [mounted, setMounted] = useState(false);
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (isOpen) {
    hasOpenedRef.current = true;
  }

  const transformRegionsToAccordionItems = (): AccordionItemData[] => {
    if (!regionSelectorData?.fields?.CountriesList) {
      return [];
    }

    return regionSelectorData.fields.CountriesList.map((region: Region) => ({
      uid: region.id,
      heading: region.fields?.regionName?.value || region.displayName,
      content: (
        <div className={styles.countriesList}>
          {(region.fields?.countriesList || []).map((country: Country) => (
            <CurrentSelection
              key={country.id}
              country={
                hasAlternateName(region?.fields?.countryNameOverride, country.displayName)
                  ? `${country.displayName} | ${hasAlternateName(region?.fields?.countryNameOverride, country.displayName)}`
                  : country.displayName
              }
              languages={getLanguagesArray(country)}
            />
          ))}
        </div>
      ),
    }));
  };
  const accordionItems = transformRegionsToAccordionItems();

  const overlayStateClass = isOpen
    ? styles.open
    : hasOpenedRef.current
      ? styles.closing
      : styles.hidden;
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const prevBodyOverflowRef = useRef<string | undefined>(undefined);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Remember the element that triggered the sheet so we can restore focus on close
      triggerRef.current = document.activeElement as HTMLElement;
      // Focus the close button when the bottom sheet opens
      setTimeout(() => closeButtonRef.current?.focus(), 0);
      // Prevent background scroll while bottom sheet is open
      prevBodyOverflowRef.current = document.body.style.overflow || undefined;
      document.body.style.overflow = 'hidden';
    }
    return () => {
      // Restore background scroll on unmount or close
      if (prevBodyOverflowRef.current !== undefined) {
        document.body.style.overflow = prevBodyOverflowRef.current;
        prevBodyOverflowRef.current = undefined;
      } else {
        document.body.style.overflow = '';
      }
      // Restore focus to the element that opened the sheet
      if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
        triggerRef.current.focus();
        triggerRef.current = null;
      }
    };
  }, [isOpen]);

  // Focus trap: document-level keydown listener to intercept Tab/Shift+Tab
  useEffect(() => {
    if (!isOpen) return;

    const FOCUSABLE_SELECTOR =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const container = overlayRef.current;
      if (!container) return;

      const focusableElements = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      );
      if (focusableElements.length === 0) return;

      const firstEl = focusableElements[0];
      const lastEl = focusableElements[focusableElements.length - 1];

      // If focus is somehow outside the sheet, bring it back
      if (!container.contains(document.activeElement)) {
        e.preventDefault();
        firstEl.focus();
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  // Render modal via portal to avoid stacking context issues with transformed elements (e.g., SVG icons)
  // Wait for client-side mount and document availability to prevent hydration mismatch
  if (!mounted || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className={`${styles.languageSelectorBottomSheetOverlay} ${overlayStateClass}`}
      data-state={isOpen ? 'open' : hasOpenedRef.current ? 'closing' : 'hidden'}
      role="dialog"
      aria-modal="true"
      aria-labelledby="region-selector-dialog-heading"
      tabIndex={-1}
      ref={overlayRef}
      onKeyDown={(e) => {
        if (!isOpen) return;
        if (e.key === 'Escape' || e.key === 'Esc') {
          e.stopPropagation();
          onClose();
          return;
        }
      }}
    >
      <div className={styles.languageSelectorSection}>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close region selector"
          ref={closeButtonRef}
        >
          <SvgIcon icon="accordionCross" />
        </button>
        {/* <div className={styles.headingContainer}> */}
        <h2 id="region-selector-dialog-heading" className={styles.bottomSheetHeading}>
          {regionSelectorData?.fields.Heading?.value}
        </h2>
        {/* </div> */}
        <div className={styles.selections}>
          <div className={styles.selector}>
            <div className={styles.locationSelector}>
              <div>
                <b className={styles.selectYourLocation}>
                  {regionSelectorData?.fields.SubHeading?.value}
                </b>
              </div>
              <div className={styles.button}>
                <SvgIcon icon="locationSelector" />

                <div className={styles.button2}>
                  <div className={styles.cta}>
                    <b className={styles.callToAction}>{currentSelection?.countryName}</b>
                  </div>
                  <div className={styles.container2}>
                    <div className={styles.selectedLanguage}>{currentSelection?.languageName}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Accordion accordionItems={accordionItems} />
        </div>
      </div>
      {/* Focus guard: catches Tab past last element and sends focus back to close button */}
      <div
        tabIndex={0}
        aria-hidden="true"
        style={{ position: 'fixed', opacity: 0, pointerEvents: 'none' }}
        onFocus={() => closeButtonRef.current?.focus()}
      />
    </div>,
    document.body
  );
};
